import axios from 'axios'
import { getAccessToken, getRefreshToken, saveTokens, clearTokens } from '@/utils/token.js'
import { requestSessionExpiredDialog, markSessionConfirmed } from '@/utils/sessionExpiredDialog.js'

/**
 * Normalize the admin API base URL.
 * API modules call /api/v1/... paths, so a configured base URL ending with
 * /api is trimmed to avoid /api/api/v1/... requests.
 */
export function normalizeAdminApiBase(raw) {
  let u = (raw || 'http://localhost:8086').trim().replace(/\/+$/, '')
  if (u.toLowerCase().endsWith('/api')) {
    u = u.slice(0, -4).replace(/\/+$/, '')
  }
  return u || 'http://localhost:8086'
}

const API_BASE = normalizeAdminApiBase(import.meta.env.VITE_API_BASE_URL)

const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use(config => {
  const token = getAccessToken()
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let pendingRequests = []

/**
 * 인증이 끊겼다. 이 페이지에서 아직 한 번도 통한 적이 없으면 모달 없이 로그인으로,
 * 쓰던 중이면 모달로 알린다(sessionExpiredDialog.js). 로그인 화면에서는 아무것도 안 띄운다.
 */
function onAuthLost() {
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  if (path !== '/login' && !path.endsWith('/login')) {
    requestSessionExpiredDialog()
  }
}

api.interceptors.response.use(
  response => {
    if (response.config?.headers?.Authorization) markSessionConfirmed()
    return response
  },
  async error => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`
          return api(originalRequest)
        })
      }

      isRefreshing = true
      const refreshToken = getRefreshToken()

      if (!refreshToken) {
        clearTokens()
        onAuthLost()
        return Promise.reject(error)
      }

      try {
        const { data } = await axios.post(
          `${API_BASE}/api/v1/auth/refresh`,
          { refreshToken },
        )

        if (data.code === 0 && data.accessToken) {
          saveTokens({ accessToken: data.accessToken, refreshToken })
          pendingRequests.forEach(p => p.resolve(data.accessToken))
          pendingRequests = []
          originalRequest.headers['Authorization'] = `Bearer ${data.accessToken}`
          return api(originalRequest)
        }
        throw new Error('refresh failed')
      } catch {
        pendingRequests.forEach(p => p.reject(error))
        pendingRequests = []
        clearTokens()
        onAuthLost()
        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }

    // 갱신한 토큰으로 다시 불렀는데도 401 - 세션이 끝났다.
    if (error.response?.status === 401) {
      clearTokens()
      onAuthLost()
      return Promise.reject(error)
    }

    // 403 은 로그인은 됐는데 그 일을 할 권한이 없다는 뜻이다(본사 전용 등). 로그인을 끊지 않고 부른 화면에 돌려준다.
    // 2026-11-03 전에는 서버가 로그인 끊김도 403 으로 줘서 여기서 '로그인 만료' 모달을 띄웠다 - 지금은 401 이다.
    return Promise.reject(error)
  },
)

export default api
